import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { RouteBreadcrumb } from '@/components/route-breadcrumb';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectActiveOrganizationId, selectCurrentUserProfile } from '@/store/selectors/authorizationSelectors';
import type { PropertyData } from '@/domain/entities/property-object';
import { type IProperty } from '@/domain/entities/property';
import { PropertyEditorTabs } from './PropertyEditorTabs';
import { selectPropertyTemplateById } from '@/features/property-templates/propertyTemplatesSlice';
import { hydrateSeededPropertyData } from '../propertyEditorUtils';
import {
  cancelPropertyHandover,
  fetchActivePropertyHandover,
  fetchPropertyById,
  initiatePropertyHandover,
} from '@/features/properties/propertiesThunks';
import {
  clearPropertyDraft,
  resetPropertyDraft,
  selectPropertyDraft,
  selectPropertyDraftIsDirty,
  selectPropertyDraftIsSaving,
  selectPropertyDraftSaveError,
  setPropertyDraft,
  setPropertyDraftSeeded,
} from '@/features/property-draft/propertyDraftSlice';
import { toast } from 'sonner';
import { batchCreatePropertyObject } from '@/features/property-draft/propertyDraftThunks';
import type { BatchCreatePropertyResponse } from '@/features/property-draft/propertyDraftTypes';
import { PropertyExpensesSummary } from './PropertyExpensesSummary';
import { PropertyStatusBox } from './PropertyStatusBox';
import { selectPropertyById, selectPropertyHandoverState } from '@/features/properties/propertiesSlice';
import { InitiateHandoverModal, type InitiateHandoverQrPayload } from '../InitiateHandoverModal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { AddressVerificationLevel } from '@/domain/shared/address';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';

type PropertyEditorLocationState = {
  fromPropertiesList?: boolean;
  fromHomeownerProperties?: boolean;
  homeownerId?: string;
  homeownerLabel?: string;
};

function cloneData<T>(data: T): T {
  return typeof structuredClone === 'function' ? structuredClone(data) : JSON.parse(JSON.stringify(data));
}

function buildEmptyDraft(): PropertyData {
  const now = Math.floor(Date.now() / 1000);
  const empty: IProperty = {
    id: `tmp-property-${now}`,
    title: '',
    description: null,
    createdAt: now,
    updatedAt: now,
    status: 'builderDraft',
    typeName: 'Property',
    handOverMeta: undefined,

    // ✅ new model fields
    orgID: undefined,
    address: {
      address: null,
      country: null,
      city: null,
      state: null,
      province: null,
      postalCode: null,
      addressVerificationLevel: AddressVerificationLevel.LEVEL_0,
      mapsAddressObject: undefined,
    },
    yearBuilt: undefined,
    numFloors: undefined,
    bedrooms: undefined,
    bathrooms: undefined,
    parkingSpots: undefined,
    lotArea: undefined,
  };
  return {
    templateID: '',
    property: empty,
    locations: [],
    utilities: [],
    systems: [],
  };
}

function buildDraftFromLoadedProperty(p: IProperty): PropertyData {
  return {
    templateID: '',
    property: p,
    locations: [],
    utilities: [],
    systems: [],
  };
}

export default function PropertyEditorPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ route: /properties/:propertyId/edit OR /properties/new
  const { propertyId } = useParams<{ propertyId: string }>();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId') ?? '';

  const isEditMode = Boolean(propertyId);
  const isCreateMode = !isEditMode;

  const currentUserProfile = useAppSelector(selectCurrentUserProfile);
  const currentUserID = currentUserProfile?.applicationUserId ?? null;
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canManageHandover = isSuperAdmin || hasPermission(Permissions.PropertiesHandover);
  const canReadMembers = isSuperAdmin || hasPermission(Permissions.MemberRead);

  // -----------------------
  // CREATE MODE: template seed
  // -----------------------
  const selectedTemplate = useAppSelector((s) => selectPropertyTemplateById(s, templateId));

  const [editDraftData, setEditDraftData] = useState<PropertyData>(() => buildEmptyDraft());
  const [editIsDirty, setEditIsDirty] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [cancelHandoverOpen, setCancelHandoverOpen] = useState(false);
  const [cancelHandoverBusy, setCancelHandoverBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // -----------------------
  // EDIT MODE: store hydrate
  // -----------------------
  const loadedProperty = useAppSelector((s) => selectPropertyById(s, propertyId));
  const isLoadingProperty = useAppSelector((s) => s.properties.isListing);
  const propertyError = useAppSelector((s) => s.properties.errorListing);
  const storeDraft = useAppSelector(selectPropertyDraft);
  const storeIsDirty = useAppSelector(selectPropertyDraftIsDirty);
  const fallbackCreateDraft = useMemo(() => buildEmptyDraft(), []);
  const draftData = isCreateMode ? (storeDraft ?? fallbackCreateDraft) : editDraftData;
  const isDirty = isCreateMode ? storeIsDirty : editIsDirty;
  const saveError = useAppSelector(selectPropertyDraftSaveError);
  const isSaving = useAppSelector(selectPropertyDraftIsSaving);
  // The code comes only from the initiate response or the properties:handover reload below.
  const handoverState = useAppSelector((s) => selectPropertyHandoverState(s, propertyId));
  const handoverCode = handoverState.data?.handover.handoverCode ?? null;
  const activeHandoverRequestKey = useRef<string | null>(null);
  const routeState =
    location.state && typeof location.state === 'object' ? (location.state as PropertyEditorLocationState) : null;
  const propertyBreadcrumbLabel = loadedProperty?.title?.trim() || propertyId || 'Property Details';
  const homeownerBreadcrumbLabel = routeState?.homeownerLabel?.trim() || 'Home Owner';
  const homeownerBreadcrumbUrl = routeState?.homeownerId
    ? `/admin/homeowners/${encodeURIComponent(routeState.homeownerId)}`
    : '/admin/homeowners';
  const propertyBreadcrumbItems = routeState?.fromHomeownerProperties
    ? [{ label: homeownerBreadcrumbLabel, to: homeownerBreadcrumbUrl }, { label: 'Property Details' }]
    : routeState?.fromPropertiesList
      ? [{ label: 'Properties', to: '/properties' }, { label: propertyBreadcrumbLabel }]
      : null;

  // Save only for create mode
  const handleSave = async () => {
    if (!draftData?.property) {
      toast.error('Draft data is missing');
      return;
    }

    try {
      if (isEditMode) {
        // You can wire this later
        toast.error('Update property is not implemented yet');
        return;
      }

      // --------------------
      // CREATE MODE
      // --------------------
      if (!currentUserID) {
        toast.error('Your application profile is not ready. Refresh and try again.');
        return;
      }

      const created = (await dispatch(
        batchCreatePropertyObject({
          property: draftData, // adjust if your API expects a different shape
          initialOwnerUserID: currentUserID,
        }),
      ).unwrap()) as BatchCreatePropertyResponse;

      const newId = created.propertyId;

      if (!newId) {
        throw new Error('Create succeeded but no property id was returned from the API.');
      }

      toast.success('Property created');

      // ✅ navigate to edit/builder page
      navigate(`/properties/${encodeURIComponent(newId)}/edit`);
    } catch (e) {
      const msg =
        typeof e === 'string' ? e : (e as Error)?.message ? String((e as Error).message) : 'Failed to save property';

      toast.error(msg);
    }
  };

  const handleReset = () => {
    if (isCreateMode) {
      dispatch(resetPropertyDraft());
      return;
    }

    if (loadedProperty) {
      setEditDraftData(buildDraftFromLoadedProperty(cloneData(loadedProperty)));
    }
    setEditIsDirty(false);
  };

  //Initiate handover flow
  const onGenerateQR = async (payload?: InitiateHandoverQrPayload) => {
    if (!loadedProperty || !canManageHandover) return;
    const { buyerEmail, note } = payload || {};

    try {
      await dispatch(
        initiatePropertyHandover({
          propertyID: loadedProperty.id,
          orgID: loadedProperty.orgID ?? '',
          buyerEmail: buyerEmail?.trim() ? buyerEmail.trim() : undefined,
          note: note?.trim() ? note.trim() : undefined,
        }),
      ).unwrap();

      // ✅ don’t auto-close anymore because QR needs to show
      // toast.success('Handover initiated');
    } catch (e) {
      const msg =
        typeof e === 'string'
          ? e
          : (e as Error)?.message
            ? String((e as Error).message)
            : 'Failed to initiate handover';
      toast.error(msg);
    }
  };

  /**
   *
   * @returns Cancel Property Handover
   */
  const onCancelPropertyHandover = async () => {
    if (!loadedProperty || !canManageHandover) return;

    // guard: only when there is an active handover
    if (loadedProperty.status !== 'handoverInitiated') {
      toast.message('No active handover to cancel.');
      return;
    }

    // open confirm dialog
    setCancelHandoverOpen(true);
  };

  const confirmCancelHandover = async () => {
    if (!loadedProperty || !canManageHandover) return;

    const activeCode =
      handoverCode ??
      (await dispatch(fetchActivePropertyHandover({ propertyID: loadedProperty.id }))
        .unwrap()
        .then((details) => details?.handover.handoverCode ?? null)
        .catch(() => null));
    if (!activeCode) {
      toast.error('No active handover to cancel.');
      setCancelHandoverOpen(false);
      return;
    }

    // basic org safety (backend will enforce too)
    const orgID = loadedProperty.orgID ?? '';
    if (!orgID) {
      toast.error('Missing orgID on property. Cannot cancel handover.');
      return;
    }

    setCancelHandoverBusy(true);

    // toast.promise gives the best UX (loading -> success/error)
    const p = dispatch(
      cancelPropertyHandover({
        propertyID: loadedProperty.id,
        orgID,
        handoverCode: activeCode,
        reason: cancelReason.trim() ? cancelReason.trim() : undefined,
      }),
    ).unwrap();

    toast.promise(p, {
      loading: 'Cancelling handover…',
      success: () => {
        // close both dialogs if needed
        setCancelHandoverOpen(false);
        setHandoverOpen(false);
        return 'Handover cancelled';
      },
      error: (e) => {
        const msg =
          typeof e === 'string'
            ? e
            : (e as Error)?.message
              ? String((e as Error).message)
              : 'Failed to cancel handover';
        setCancelHandoverOpen(false);
        setHandoverOpen(false);
        return msg;
      },
    });

    try {
      await p;
    } finally {
      setCancelHandoverBusy(false);
    }
  };

  // ✅ Create mode: seed once template is available
  useEffect(() => {
    if (!isCreateMode) return;

    if (selectedTemplate?.data && currentUserID) {
      const seed = cloneData(selectedTemplate.data);

      const hydrated = hydrateSeededPropertyData({
        seed: {
          ...seed,
          templateID: selectedTemplate.id,
        },
        currentUserID,
        orgID: activeOrganizationId ?? undefined,
      });

      dispatch(setPropertyDraftSeeded({ seed: hydrated }));
      return;
    }

    dispatch(setPropertyDraftSeeded({ seed: buildEmptyDraft() }));
  }, [
    dispatch,
    isCreateMode,
    selectedTemplate?.data,
    selectedTemplate?.id,
    selectedTemplate?.updatedAt,
    activeOrganizationId,
    currentUserID,
  ]);

  // ✅ Edit mode: fetch property if missing
  useEffect(() => {
    if (!isEditMode) return;
    if (!propertyId) return;

    // If already in store, don't fetch
    if (loadedProperty) return;

    // TODO: uncomment when thunk exists
    dispatch(fetchPropertyById({ id: propertyId }));

    // Temporary: do nothing (assumes list page already loaded items)
  }, [dispatch, isEditMode, propertyId, loadedProperty]);

  // ✅ Edit mode: when property arrives, hydrate draft (once)
  useEffect(() => {
    if (!isEditMode) return;
    if (!loadedProperty) return;

    setEditDraftData(buildDraftFromLoadedProperty(cloneData(loadedProperty)));
    setEditIsDirty(false);
  }, [isEditMode, loadedProperty]);

  // ✅ Edit mode: after a refresh, reload an in-flight handover through the authorized operation
  const loadedPropertyStatus = loadedProperty?.status;
  const hasHandoverDetails = Boolean(handoverState.data);
  useEffect(() => {
    if (!canManageHandover || !isEditMode || !propertyId) return;
    if (loadedPropertyStatus !== 'handoverInitiated' || hasHandoverDetails) return;
    const requestKey = `${propertyId}:${loadedPropertyStatus}`;
    if (activeHandoverRequestKey.current === requestKey) return;
    activeHandoverRequestKey.current = requestKey;
    dispatch(fetchActivePropertyHandover({ propertyID: propertyId }));
  }, [canManageHandover, dispatch, isEditMode, propertyId, loadedPropertyStatus, hasHandoverDetails]);

  useEffect(() => {
    return () => {
      dispatch(clearPropertyDraft());
    };
  }, [dispatch]);

  const headerTitle = isCreateMode ? 'New Property' : 'Edit Property';
  const headerDesc = isCreateMode
    ? `Base template: ${selectedTemplate?.title ?? (templateId || 'None')}`
    : isLoadingProperty
      ? `Loading property: ${propertyId ?? ''}`
      : propertyError
        ? `Failed to load property: ${propertyId ?? ''}`
        : `Property: ${propertyId ?? ''}`;

  // ✅ EDIT MODE: block UI if we can't load the property
  if (isEditMode) {
    // loading state (optional)
    if (isLoadingProperty && !loadedProperty) {
      return (
        <div className="space-y-6">
          {propertyBreadcrumbItems ? <RouteBreadcrumb items={propertyBreadcrumbItems} separator=">" /> : null}
          <PageHeader title="Edit Property" description={`Loading property: ${propertyId ?? ''}`} />
          <div className="text-muted-foreground text-sm">Loading…</div>
        </div>
      );
    }

    // error / not found state => NO tabs
    if (propertyError || !loadedProperty) {
      const msg = propertyError
        ? `Failed to load property: ${propertyId ?? ''}`
        : `Property not found: ${propertyId ?? ''}`;

      return (
        <div className="space-y-6">
          {propertyBreadcrumbItems ? <RouteBreadcrumb items={propertyBreadcrumbItems} separator=">" /> : null}
          <PageHeader title="Edit Property" description={msg} />
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border p-3 text-sm">
            {msg}
          </div>
        </div>
      );
    }
  }
  return (
    <div className="space-y-6">
      {propertyBreadcrumbItems ? <RouteBreadcrumb items={propertyBreadcrumbItems} separator=">" /> : null}

      <PageHeader
        title={headerTitle}
        description={headerDesc}
        right={
          isCreateMode ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset} disabled={!isDirty}>
                Reset
              </Button>
              <Button onClick={handleSave} disabled={!isDirty || isSaving}>
                Save
              </Button>
            </div>
          ) : null
        }
      />

      {loadedProperty ? (
        <>
          <PropertyStatusBox
            propertyId={loadedProperty.id}
            propertyCode={loadedProperty.propertyCode}
            status={loadedProperty.status}
            ownedSince={
              loadedProperty.currentOwnershipStartedAt ??
              loadedProperty.handOverMeta?.transferredAt ??
              loadedProperty.createdAt
            }
            isBuilderContext={Boolean(loadedProperty.orgID)} // replace with your real check (myOrgID === createdByOrgID)
            canManageHandover={canManageHandover}
            canReadMembers={canReadMembers}
            onInitiateTransfer={() => setHandoverOpen(true)}
            onCancelHandover={canManageHandover ? onCancelPropertyHandover : undefined}
            onViewHandover={() => setHandoverOpen(true)}
          />

          <PropertyExpensesSummary propertyId={loadedProperty.id} />
        </>
      ) : null}

      {isSaving ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <span className="animate-spin">⏳</span>
          Creating property…
        </div>
      ) : null}

      {saveError ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-lg border p-3 text-sm whitespace-pre-wrap">
          {saveError}
        </div>
      ) : null}

      <PropertyEditorTabs
        data={draftData}
        setData={(next) => {
          if (isCreateMode) {
            dispatch(setPropertyDraft(next));
          } else {
            setEditDraftData(next);
            setEditIsDirty(true);
          }
        }}
        mode={isCreateMode ? 'create' : 'edit'}
        propertyIdFromRoute={propertyId}
      />

      {loadedProperty && canManageHandover ? (
        <InitiateHandoverModal
          open={handoverOpen}
          propertyStatus={loadedProperty.status}
          onOpenChange={setHandoverOpen}
          propertyTitle={loadedProperty.title || 'Untitled'}
          propertyId={loadedProperty.id}
          onGenerateQr={onGenerateQR}
          onSubmitInvite={async () => {
            toast.error('Not supported yet');
          }}
        />
      ) : null}

      <Dialog
        open={canManageHandover && cancelHandoverOpen}
        onOpenChange={(v) => !cancelHandoverBusy && setCancelHandoverOpen(v)}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Cancel handover?</DialogTitle>
            <DialogDescription>
              This will invalidate the current QR/code and the buyer won’t be able to claim the property unless you
              generate a new handover.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm font-medium">Reason (optional)</label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Buyer backed out, wrong email, timing changed…"
              rows={3}
              disabled={cancelHandoverBusy}
            />
            <p className="text-muted-foreground text-xs">
              This will be recorded in the activity log and visible to admins.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCancelHandoverOpen(false);
                setCancelReason('');
              }}
              disabled={cancelHandoverBusy}
            >
              Keep it
            </Button>
            <Button type="button" variant="destructive" onClick={confirmCancelHandover} disabled={cancelHandoverBusy}>
              {cancelHandoverBusy ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Cancelling…
                </span>
              ) : (
                'Cancel handover'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
